import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';

const STATUS_LABELS = { open: '🟢 Açık', closed: '🔴 Kapalı', suspended: '🟡 Askıda' };
const PHASE_COLORS = { 'I': '#7c3aed', 'II': '#0284c7', 'III': '#0891b2', 'IV': '#059669' };
const ARM_COLORS = ['#0891b2', '#7c3aed', '#059669', '#d97706', '#dc2626', '#6366f1'];

export default function TrialDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trial, setTrial] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [investigators, setInvestigators] = useState([]);
  const [arms, setArms] = useState([]);
  const [trialDiseases, setTrialDiseases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [id]);

  async function fetchData() {
    setLoading(true);
    const [trialRes, criteriaRes, armsRes, invRes, diseasesRes] = await Promise.all([
      supabase.from('trials').select('*, branches(name), reviewer:profiles!trials_criteria_reviewed_by_fkey(full_name)').eq('id', id).single(),
      supabase.from('criteria').select('*').eq('trial_id', id).order('order_index'),
      supabase.from('trial_arms').select('*').eq('trial_id', id).order('order_index'),
      supabase.from('trial_investigators').select('*').eq('trial_id', id).order('order_index'),
      supabase.from('trial_diseases').select('diseases(id, name, code)').eq('trial_id', id),
    ]);
    setTrial(trialRes.data);
    setCriteria(criteriaRes.data || []);
    setArms(armsRes.data || []);
    setInvestigators(invRes.data || []);
    setTrialDiseases(diseasesRes.data?.map(td => td.diseases).filter(Boolean) || []);
    setLoading(false);
  }

  if (loading) return <div style={S.center}>Yükleniyor...</div>;
  if (!trial) return <div style={S.center}>Çalışma bulunamadı.</div>;

  const inclusionCriteria = criteria.filter(c => c.type === 'inclusion');
  const exclusionCriteria = criteria.filter(c => c.type === 'exclusion');
  const keyCriteria = criteria.filter(c => c.is_key);
  const nctUrl = trial.nct_number ? 'https://clinicaltrials.gov/study/' + trial.nct_number : '';

  return (
    <div style={S.container}>
      <button style={S.backBtn} onClick={() => navigate('/')}>← Listeye Dön</button>

      <div style={S.headerCard}>
        <div style={S.headerTop}>
          <div style={S.badges}>
            <span style={{ ...S.phaseBadge, background: PHASE_COLORS[trial.phase] || '#64748b' }}>Faz {trial.phase || '?'}</span>
            <span style={S.statusBadge}>{STATUS_LABELS[trial.status]}</span>
            {trial.is_randomized && <span style={S.randomBadge}>🔀 Randomize</span>}
            {trial.branches && <span style={S.branchBadge}>🏥 {trial.branches.name}</span>}
            {trialDiseases.map(d => <span key={d.id} style={S.diseaseBadge}>🧬 {d.name}</span>)}
          </div>
          {trial.nct_number && (
            <a href={nctUrl} target="_blank" rel="noreferrer" style={S.nctLink}>{trial.nct_number} ↗ ClinicalTrials.gov</a>
          )}
        </div>
        <h1 style={S.title}>{trial.title}</h1>
        {trial.short_code && <p style={S.shortCode}>{trial.short_code}</p>}
        <div style={S.metaGrid}>
          {trial.start_date && <div style={S.metaItem}><span style={S.metaLabel}>📅 Başlangıç</span><span style={S.metaValue}>{new Date(trial.start_date).toLocaleDateString('tr-TR')}</span></div>}
          {trial.estimated_end_date && <div style={S.metaItem}><span style={S.metaLabel}>🏁 Tahmini Bitiş</span><span style={S.metaValue}>{new Date(trial.estimated_end_date).toLocaleDateString('tr-TR')}</span></div>}
        </div>
        {trial.notes && <div style={S.notes}><strong>📝 Notlar:</strong> {trial.notes}</div>}
      </div>

      {arms.length > 0 && (
        <div style={S.section}>
          <h2 style={S.sectionTitle}>💊 Çalışma Kolları <span style={S.countBadge}>{arms.length} kol</span></h2>
          <div style={S.armsGrid}>
            {arms.map((arm, i) => (
              <div key={arm.id} style={{ ...S.armCard, borderTop: '4px solid ' + ARM_COLORS[i % ARM_COLORS.length] }}>
                <div style={S.armHeader}>
                  <span style={{ ...S.armBadge, background: ARM_COLORS[i % ARM_COLORS.length] }}>Kol {i + 1}</span>
                  <span style={S.armName}>{arm.name}</span>
                </div>
                <div style={S.armField}>
                  <span style={S.armFieldLabel}>💊 İlaç / Ajanlar</span>
                  <p style={S.armFieldValue}>{arm.agents}</p>
                </div>
                {arm.description && (
                  <div style={{ ...S.armField, borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem' }}>
                    <span style={S.armFieldLabel}>📋 Rejim / Doz</span>
                    <p style={{ ...S.armFieldValue, color: '#64748b' }}>{arm.description}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {investigators.length > 0 && (
        <div style={S.section}>
          <h2 style={S.sectionTitle}>🏥 Merkezler ve Araştırmacılar <span style={S.countBadge}>{investigators.length} merkez</span></h2>
          <div style={S.invGrid}>
            {investigators.map((inv, i) => (
              <div key={inv.id} style={S.invCard}>
                <div style={S.invCardHeader}>
                  <span style={S.invNum}>{i + 1}</span>
                  <div>
                    <h4 style={S.invCenterName}>{inv.center_name}</h4>
                    {inv.city && <span style={S.invCity}>📍 {inv.city}</span>}
                  </div>
                </div>
                <div style={S.invDetails}>
                  {inv.investigator && <div style={S.invDetailRow}><span style={S.invDetailIcon}>👤</span><span style={S.invDetailText}>{inv.investigator}</span></div>}
                  {inv.phone && <div style={S.invDetailRow}><span style={S.invDetailIcon}>📞</span><a href={'tel:' + inv.phone} style={S.invDetailLink}>{inv.phone}</a></div>}
                  {inv.email && <div style={S.invDetailRow}><span style={S.invDetailIcon}>✉️</span><a href={'mailto:' + inv.email} style={S.invDetailLink}>{inv.email}</a></div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={S.section}>
        <h2 style={S.sectionTitle}>📋 Dahil / Hariç Tutma Kriterleri</h2>
        {criteria.some(c => c.source === 'protocol_ai') && (
          <p style={S.aiNote}>Kriterler protokolden özetlenmiştir; birebir protokol metni değildir.{trial.criteria_reviewed_at && <> Kontrol eden: <strong>{trial.reviewer?.full_name || '—'}</strong>, {new Date(trial.criteria_reviewed_at).toLocaleDateString('tr-TR')}.</>}</p>
        )}
        {keyCriteria.length > 0 && (
          <div style={S.keyBox}>
            <h3 style={S.keyTitle}>⭐ Kritik ön tarama kriterleri</h3>
            {keyCriteria.map(c => (
              <div key={c.id} style={S.keyRow}>
                <span style={{ ...S.keyType, background: c.type === 'inclusion' ? '#dcfce7' : '#fee2e2', color: c.type === 'inclusion' ? '#166534' : '#b91c1c' }}>{c.type === 'inclusion' ? 'Dahil' : 'Hariç'}</span>
                <span style={S.criteriaText}>{c.description}</span>
              </div>
            ))}
          </div>
        )}
        <div style={S.criteriaGrid}>
          <div>
            <h3 style={S.criteriaHeader}>✅ Dahil Etme Kriterleri ({inclusionCriteria.length})</h3>
            <div style={S.criteriaList}>
              {inclusionCriteria.length > 0
                ? inclusionCriteria.map((c, i) => (
                  <div key={c.id} style={S.criteriaItem}>
                    <span style={S.criteriaNum}>{i + 1}</span>
                    <span style={S.criteriaText}>{c.description}</span>
                  </div>
                ))
                : <p style={S.empty}>Dahil etme kriteri girilmemiş.</p>}
            </div>
          </div>
          <div>
            <h3 style={S.criteriaHeaderRed}>❌ Hariç Tutma Kriterleri ({exclusionCriteria.length})</h3>
            <div style={S.criteriaList}>
              {exclusionCriteria.length > 0
                ? exclusionCriteria.map((c, i) => (
                  <div key={c.id} style={{ ...S.criteriaItem, background: '#fef2f2' }}>
                    <span style={{ ...S.criteriaNum, background: '#fee2e2', color: '#dc2626' }}>{i + 1}</span>
                    <span style={S.criteriaText}>{c.description}</span>
                  </div>
                ))
                : <p style={S.empty}>Hariç tutma kriteri girilmemiş.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  container: { padding: '2rem', maxWidth: '1000px', margin: '0 auto' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', fontSize: '1.1rem', color: '#64748b' },
  backBtn: { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.9rem', marginBottom: '1.5rem', padding: 0 },
  headerCard: { background: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1.5rem', border: '1px solid #e2e8f0' },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' },
  badges: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  phaseBadge: { color: 'white', padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '600' },
  statusBadge: { fontSize: '0.85rem', color: '#475569', padding: '0.25rem 0.75rem', background: '#f1f5f9', borderRadius: '999px' },
  randomBadge: { fontSize: '0.8rem', color: '#7c3aed', padding: '0.25rem 0.75rem', background: '#f3e8ff', borderRadius: '999px' },
  branchBadge: { fontSize: '0.8rem', color: '#7c3aed', padding: '0.25rem 0.75rem', background: '#f3e8ff', borderRadius: '999px' },
  diseaseBadge: { fontSize: '0.8rem', color: '#065f46', padding: '0.25rem 0.75rem', background: '#d1fae5', borderRadius: '999px' },
  nctLink: { fontSize: '0.85rem', color: '#3b82f6', textDecoration: 'none' },
  title: { fontSize: '1.5rem', color: '#1e293b', margin: '0 0 0.25rem', lineHeight: 1.4 },
  shortCode: { fontSize: '0.9rem', color: '#94a3b8', margin: '0 0 1.25rem' },
  metaGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', margin: '1.25rem 0' },
  metaItem: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  metaLabel: { fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' },
  metaValue: { fontSize: '0.9rem', color: '#1e293b', fontWeight: '500' },
  notes: { background: '#f8fafc', padding: '1rem', borderRadius: '8px', fontSize: '0.9rem', color: '#475569', marginTop: '1rem' },
  section: { background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1.5rem', border: '1px solid #e2e8f0' },
  sectionTitle: { fontSize: '1.1rem', color: '#1e3a5f', margin: '0 0 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
  countBadge: { background: '#e0f2fe', color: '#0369a1', padding: '0.15rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600' },
  armsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' },
  armCard: { background: '#f8fafc', borderRadius: '10px', padding: '1.25rem', border: '1px solid #e2e8f0' },
  armHeader: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' },
  armBadge: { color: 'white', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0 },
  armName: { fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' },
  armField: { marginBottom: '0.75rem' },
  armFieldLabel: { fontSize: '0.7rem', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '0.25rem' },
  armFieldValue: { margin: 0, fontSize: '0.9rem', color: '#1e293b', fontWeight: '500', lineHeight: 1.5 },
  invGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' },
  invCard: { background: '#f0fdf4', borderRadius: '10px', padding: '1.25rem', border: '1px solid #bbf7d0' },
  invCardHeader: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.875rem' },
  invNum: { background: '#0f766e', color: 'white', width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: '700', flexShrink: 0 },
  invCenterName: { margin: '0 0 0.2rem', color: '#1e293b', fontSize: '0.95rem', fontWeight: '600' },
  invCity: { fontSize: '0.78rem', color: '#64748b' },
  invDetails: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  invDetailRow: { display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.45rem' },
  invDetailIcon: { fontSize: '0.85rem', flexShrink: 0 },
  invDetailText: { fontSize: '0.875rem', color: '#334155', lineHeight: '1.6' },
  invDetailLink: { fontSize: '0.875rem', color: '#1d4ed8', textDecoration: 'none', fontWeight: '500', lineHeight: '1.6' },
  criteriaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' },
  criteriaHeader: { fontSize: '0.95rem', color: '#065f46', marginBottom: '0.875rem', fontWeight: '700' },
  criteriaHeaderRed: { fontSize: '0.95rem', color: '#dc2626', marginBottom: '0.875rem', fontWeight: '700' },
  criteriaList: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  criteriaItem: { display: 'flex', gap: '0.75rem', alignItems: 'flex-start', background: '#f0fdf4', padding: '0.875rem', borderRadius: '8px' },
  criteriaNum: { background: '#bbf7d0', color: '#166534', width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0 },
  criteriaText: { fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.5 },
  aiNote: { fontSize: '0.8rem', color: '#6d28d9', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '0.5rem 0.75rem', margin: '-0.5rem 0 1rem' },
  keyBox: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem' },
  keyTitle: { fontSize: '0.95rem', color: '#92400e', margin: '0 0 0.6rem', fontWeight: 700 },
  keyRow: { display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.35rem 0' },
  keyType: { fontSize: '0.7rem', fontWeight: 700, borderRadius: '6px', padding: '0.15rem 0.45rem', flexShrink: 0, marginTop: '0.15rem' },
  empty: { color: '#94a3b8', fontSize: '0.9rem', fontStyle: 'italic' },
};
